import router from "next/router";
import { AudioType, Size } from "@/models/enums";
import { useAudioPlayer } from "@/src/contexts/AudioPlayerContext";
import KitArt from "@/components/arts/kit-art";
import TrackArt from "@/components/arts/track-art";
import BlackButton from "@/components/buttons/black-button";
import ColoredPill from "@/components/pills/colored-pill";
import Link from "next/link";
import { getViewObjectUrl, isBeat, slugify } from "@/lib/helpers";
import { Beat, SoundKit, ViewObject } from "@/models/models";
import PricingPopup from "@/components/detail-page/pricing-popup";
import { useState } from "react";
import AddCart from "@/components/icons/lib/add-shopping-cart";

import { CREATORS_SLUG_ID, VIEW_CATEGORY_SLUG_ID } from "@/constants/routes";

interface ListTileProps {
  type: AudioType;
  item?: Beat | SoundKit;
  playableTrack?: boolean;
  hoverEffect?: boolean;
  onClick?: Function;
  onPlay?: (item: ViewObject) => void;
  className?: string;
}

const ListTile = ({
  item,
  type,
  playableTrack = false,
  hoverEffect = false,
  onClick,
  onPlay,
  className,
}: ListTileProps) => {
  // hooks
  const audioPlayer = useAudioPlayer();

  // states
  const [pricePopupVisible, setPricePopupVisible] = useState(false);

  const handlePlay = () => {
    if (item) {
      onPlay?.(item);
    }

    if (
      item &&
      ((item?.type === "sounds" && item?.sample) || item?.type !== "sounds")
    ) {
      audioPlayer.handlePlayTrack(item);
      onClick && onClick();
    }
  };

  if (!item) {
    return <div className="flex w-full h-full" />;
  } else {
    return (
      <div
        className={`flex justify-between lg:grid grid-cols-3 lg:grid-cols-7 2xl:grid-cols-6 gap-3 items-center w-full rounded-2xl ${
          hoverEffect && "hover:bg-backgroundLight transition-all"
        } ${
          audioPlayer.selectedAudio?.id === item.id && "bg-backgroundLight"
        } ${className}`}
      >
        {/* Image and text */}
        <div
          className="flex space-x-4 items-center lg:col-span-3 2xl:col-span-2 w-[80%] truncate py-3 pl-3 lg:py-5 lg:pl-5"
          onClick={() => (onClick ? onClick() : "")}
        >
          {type === AudioType.track ? (
            <TrackArt
              id={`list-trackArt-play-${item.id}`}
              item={item as Beat}
              size={Size.sm}
              onPlay={handlePlay}
              onClick={() => router.push(getViewObjectUrl(item))}
              play={playableTrack}
            />
          ) : (
            <KitArt
              id={`list-soundkitArt-play-${item.id}`}
              item={item as SoundKit}
              size={Size.sm}
              play={item.sample ? playableTrack : false}
              onPlay={handlePlay}
              onClick={() => router.push(getViewObjectUrl(item))}
            />
          )}
          <div className="flex flex-col space-y-1 items-start justify-between w-[50%] lg:w-2/3">
            {type === AudioType.track && (
              <ColoredPill
                text={item.tag?.name!}
                textColor={item.tag?.textColor!}
                backgroundColor={item.tag?.backgroundColor!}
              />
            )}
            <Link
              id={`list-button-trackName-${item.id}`}
              href={{
                pathname: VIEW_CATEGORY_SLUG_ID,
                query: {
                  category: type === AudioType.kit ? "sounds" : isBeat(item) ? "beats" : "songs", 
                  slug: slugify(item.name), 
                  id: item.id
                }
              }}
              className="w-full text-start hover:underline"
            >
              <h4
                className={`text-start text-base lg:text-xl font-bold w-full hover:underline min-w-[117px] truncate hover:animate-marquee-fast`}
              >
                {item.name}
              </h4>
            </Link>

            <button
              id={`list-button-creatorName-${item.artist?.id}`}
              className="w-full text-start"
              onClick={() =>
                router.push({
                  pathname: CREATORS_SLUG_ID,
                  query: {
                    slug: slugify(item.artist?.name),
                    id: item.artist?.id
                  }
                })
              }
            >
              <p className="text-gray hover:underline truncate">
                {item.artist?.name}
              </p>
            </button>
          </div>
        </div>

        {/* Contents */}
        {type === AudioType.kit && (
          <div className="hidden lg:flex justify-center col-span-2"></div>
        )}

        {/* Tempo */}
        {type === AudioType.track && item.tempo && (
          <div className="hidden lg:flex justify-between col-span-2">
            <p className="font-semibold">{item.styles?.at(0)?.name} </p>
            <p className="text-subtitle">{item.tempo} BPM</p>
          </div>
        )}

        {/* Add to cart button */}
        <div className="space-x-5 py-3 lg:py-5 pr-3 lg:pr-5 items-center flex justify-end col-span-2">
          {/* <Icon icon={Heart} /> */}

          <BlackButton
            id={`list-btn-addToCart-${item.id}`}
            className="list-btn-addToCart"
            text={`$${item.price?.toFixed(2)}`}
            icon={AddCart}
            onClick={() => setPricePopupVisible(true)}
          />
        </div>

        {/* Mobile price and option button
        <MobileInfo item={item} type={type} showPrice /> */}
        <PricingPopup
          item={item}
          isProductDetail
          isOpen={pricePopupVisible}
          closeModal={() => {
            setPricePopupVisible(false);
          }}
          closeMessage="Go back"
        />
      </div>
    );
  }
};

export default ListTile;
